import React, { useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { BarChart3Icon, StethoscopeIcon, DownloadIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Appointment, Doctor, Service } from '../../types';
import { formatCurrency, getToday, getDoctorPrefix } from '../../utils/helpers';
import { exportRevenueRankingToPdf } from '../../utils/pdfExport';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
interface RevenueByUserProps {
  appointments: Appointment[];
  getService: (id: string) => Service | undefined;
}
export function DashboardRevenueByUser({
  appointments,
  getService
}: RevenueByUserProps) {
  const {
    user
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const chartAxisColor = isDark ? '#9ca3af' : '#9ca3af';
  const chartTickColor = isDark ? '#d1d5db' : '#374151';
  const chartTooltipBg = isDark ? '#1f2937' : '#ffffff';
  const chartTooltipBorder = isDark ? '1px solid #374151' : 'none';
  const chartTooltipColor = isDark ? '#f9fafb' : '#111827';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canDownloadPdf = user?.role === 'administrador' || user?.role === 'supervisor/a' || user?.role === 'Terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [revSelectedUserId, setRevSelectedUserId] = useState('all');
  const [revDateFrom, setRevDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [revDateTo, setRevDateTo] = useState<string>(getToday());
  const revenueByUserStats = useMemo(() => {
    const filteredApts = appointments.filter(a => {
      return a.status === 'completada' && (!revDateFrom || a.date >= revDateFrom) && (!revDateTo || a.date <= revDateTo) && (revSelectedUserId === 'all' || a.createdByUserId === revSelectedUserId);
    });
    const revenueMap: Record<string, number> = {};
    filteredApts.forEach(apt => {
      const userName = apt.createdByUserName || 'Sin registrar';
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      let aptTotal = 0;
      serviceIds.forEach(sId => {
        const s = getService(sId);
        if (s) {
          const discountPercent = Number(apt.serviceDiscounts?.[sId] || 0);
          aptTotal += Math.max(0, s.price - s.price * (discountPercent / 100));
        }
      });
      if (aptTotal > 0) revenueMap[userName] = (revenueMap[userName] || 0) + aptTotal;
    });
    return Object.entries(revenueMap).map(([name, amount]) => ({
      name,
      amount
    })).sort((a, b) => b.amount - a.amount);
  }, [appointments, getService, revDateFrom, revDateTo, revSelectedUserId]);
  return <Card padding="none" className="overflow-hidden" data-id="element-2909" data-mp="i6ghdb5gnvq4">
      <div className="h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" data-id="element-2910" data-mp="45w04jf727yq" />
      <div className="p-4 md:p-6" data-id="element-2911" data-mp="488pvir71mmc">
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2912" data-mp="86pn9aktbiqk">
          <CardTitle className="flex items-center gap-2" data-id="element-2913" data-mp="vy3e1c3bf69f">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg shadow-emerald-500/20" data-id="element-2914" data-mp="10w580s2a4pzo">
              <BarChart3Icon className="w-5 h-5 text-white" data-id="element-2915" data-mp="n5jnkyx6g7zo" />
            </div>
            <div data-id="element-2916" data-mp="r63kq2hr3blt">
              <span className="block" data-id="element-2917" data-mp="r5k1migd6h67">Ingresos por Usuario</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-2918" data-mp="r3z7gtxyp08v">
                Citas completadas
              </span>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2" data-id="element-2919" data-mp="1jnxr9urjqwu7">
            {canDownloadPdf && revenueByUserStats.length > 0 && <button onClick={() => {
            exportRevenueRankingToPdf(revenueByUserStats, 'Ingresos por Usuario', {
              from: revDateFrom,
              to: revDateTo
            });
          }} className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2920" data-mp="3s1mbnd4f2rvk">
                <DownloadIcon className="w-4 h-4" data-id="element-2921" data-mp="enhmurzfvdye" />
              </button>}
            <Badge className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-1.5 px-3 text-sm shadow-lg shadow-emerald-500/20 border-none" data-id="element-2922" data-mp="9p7sgjljghdu">
              {formatCurrency(revenueByUserStats.reduce((sum, item) => sum + item.amount, 0))}
            </Badge>
          </div>
        </CardHeader>

        <div className="flex flex-wrap items-center gap-2 mt-4 mb-5" data-id="element-2923" data-mp="xfk7eef7bedx">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2924" data-mp="solod72tw26z">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDateFrom} onChange={e => setRevDateFrom(e.target.value)} data-id="element-2925" data-mp="2bcdevwfr8zn2" />
            <span className="text-gray-300 text-xs" data-id="element-2926" data-mp="2kayrzc2gjmgg">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDateTo} onChange={e => setRevDateTo(e.target.value)} data-id="element-2927" data-mp="2pb3d5feb95d0" />
          </div>
          <select className="text-xs border rounded-lg p-1.5 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 focus:ring-emerald-500 text-gray-700 dark:text-gray-300" value={revSelectedUserId} onChange={e => setRevSelectedUserId(e.target.value)} data-id="element-2928" data-mp="2z90wppl86dza">
            <option value="all" data-id="element-2929" data-mp="3812u1ezzze5c">Todos</option>
            {Array.from(new Set(appointments.map(a => a.createdByUserId).filter(Boolean))).map(userId => {
            const apt = appointments.find(a => a.createdByUserId === userId);
            return <option key={userId} value={userId} data-id="element-2930" data-mp="1hx9di0zkawz2">
                  {apt?.createdByUserName || userId}
                </option>;
          })}
          </select>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-2931" data-mp="1cwd3d5omael7">
          {revenueByUserStats.length > 0 && <div className="mb-6" data-id="element-2932" data-mp="1rox2zwck63xw">
              <ResponsiveContainer width="100%" height={240} data-id="element-2933" data-mp="1mwm1ip266a38">
                <BarChart data={revenueByUserStats.slice(0, 6)} layout="vertical" margin={{
              left: 0,
              right: 10
            }} data-id="element-2934" data-mp="1mvaxco4tb0bl">
                  <defs data-id="element-2935" data-mp="1wmyjqamihr67">
                    <linearGradient id="userRevenueGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-2936" data-mp="1wlngqd0w4ng5">
                      <stop offset="0%" stopColor="#10b981" data-id="element-2937" data-mp="2al58905kmet7" />
                      <stop offset="100%" stopColor="#14b8a6" data-id="element-2938" data-mp="25n3pkgc24hi9" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} horizontal={true} vertical={false} data-id="element-2939" data-mp="g7dqfdd38omo" />
                  <XAxis type="number" stroke={chartAxisColor} fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2940" data-mp="ba44i7owqv3l" />
                  <YAxis dataKey="name" type="category" stroke={chartAxisColor} fontSize={12} tickLine={false} axisLine={false} width={110} tick={{
                fill: chartTickColor,
                fontWeight: 500
              }} data-id="element-2941" data-mp="2rv9706mmdt7x" />
                  <Tooltip contentStyle={{
                backgroundColor: chartTooltipBg,
                border: chartTooltipBorder,
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                padding: '10px 14px',
                color: chartTooltipColor
              }} formatter={(value: number) => [<span style={{
                fontWeight: 'bold'
              }} data-id="element-2943" data-mp="1ua4qkww97p7s">{formatCurrency(value)}</span>, 'Ingresos']} data-id="element-2942" data-mp="2i4ej6xtq6lkd" />
                  <Bar dataKey="amount" fill="url(#userRevenueGrad)" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-2944" data-mp="3ol9tfyzinxm2" />
                </BarChart>
              </ResponsiveContainer>
            </div>}
          <div className="space-y-4" data-id="element-2945" data-mp="2hb9fxqp3cihp">
            {revenueByUserStats.map((user, index) => {
            const maxAmount = Math.max(...revenueByUserStats.map(u => u.amount), 1);
            const medals = ['🥇', '🥈', '🥉'];
            const medal = index < 3 ? medals[index] : null;
            return <div key={user.name} className="flex items-center gap-3 px-1 group" data-id="element-2946" data-mp="22mms10leeiev" data-mpi={index}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm ${index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' : index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' : index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-800'}`} data-id="element-2947" data-mp="22ksc60ozhghn">
                    {medal || index + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2948" data-mp="3e339ezgij94d">
                    <div className="flex justify-between mb-1.5" data-id="element-2949" data-mp="3llasxi3cggrn">
                      <span className="text-[15px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-2950" data-mp="3ll12xuiau9ly">
                        {user.name}
                      </span>
                      <span className="text-[15px] font-bold text-emerald-600 dark:text-emerald-400 ml-2 whitespace-nowrap" data-id="element-2951" data-mp="3vc5k90vg66jy">
                        {formatCurrency(user.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden" data-id="element-2952" data-mp="32ojfhpahgvvi">
                      <div className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-emerald-500 to-teal-400" style={{
                    width: `${user.amount / maxAmount * 100}%`
                  }} data-id="element-2953" data-mp="37nctditomow7" />
                    </div>
                  </div>
                </div>;
          })}
            {revenueByUserStats.length === 0 && <div className="text-center py-10 text-gray-400 text-sm italic" data-id="element-2954" data-mp="wsfa64303fvn">
                No se encontraron ingresos para este filtro.
              </div>}
          </div>
        </CardContent>
      </div>
    </Card>;
}
interface RevenueByDoctorProps {
  appointments: Appointment[];
  doctors: Doctor[];
  getService: (id: string) => Service | undefined;
  getDoctor: (id: string) => Doctor | undefined;
}
export function DashboardRevenueByDoctor({
  appointments,
  doctors,
  getService,
  getDoctor
}: RevenueByDoctorProps) {
  const {
    user
  } = useAuth();
  const {
    getSpecialty
  } = useData();
  const {
    theme: theme2
  } = useTheme();
  const isDark2 = theme2 === 'dark';
  const chartGridColor2 = isDark2 ? '#374151' : '#e5e7eb';
  const chartAxisColor2 = isDark2 ? '#9ca3af' : '#9ca3af';
  const chartTickColor2 = isDark2 ? '#d1d5db' : '#374151';
  const chartTooltipBg2 = isDark2 ? '#1f2937' : '#ffffff';
  const chartTooltipBorder2 = isDark2 ? '1px solid #374151' : 'none';
  const chartTooltipColor2 = isDark2 ? '#f9fafb' : '#111827';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canDownloadPdf = user?.role === 'administrador' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [revDoctorDateFrom, setRevDoctorDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [revDoctorDateTo, setRevDoctorDateTo] = useState<string>(getToday());
  const [revDoctorSelectedId, setRevDoctorSelectedId] = useState<string>('all');
  const revenueByDoctorStats = useMemo(() => {
    const filteredApts = appointments.filter(a => {
      return a.status === 'completada' && (!revDoctorDateFrom || a.date >= revDoctorDateFrom) && (!revDoctorDateTo || a.date <= revDoctorDateTo) && (revDoctorSelectedId === 'all' || a.doctorId === revDoctorSelectedId);
    });
    const revenueMap: Record<string, {
      name: string;
      amount: number;
      count: number;
    }> = {};
    filteredApts.forEach(apt => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : 'Sin asignar';
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      let aptTotal = 0;
      serviceIds.forEach(sId => {
        const s = getService(sId);
        if (s) {
          const discountPercent = Number(apt.serviceDiscounts?.[sId] || 0);
          aptTotal += Math.max(0, s.price - s.price * (discountPercent / 100));
        }
      });
      if (aptTotal > 0) {
        if (!revenueMap[doctorName]) revenueMap[doctorName] = {
          name: doctorName,
          amount: 0,
          count: 0
        };
        revenueMap[doctorName].amount += aptTotal;
        revenueMap[doctorName].count += 1;
      }
    });
    return Object.values(revenueMap).sort((a, b) => b.amount - a.amount);
  }, [appointments, getService, getDoctor, revDoctorDateFrom, revDoctorDateTo, revDoctorSelectedId]);
  return <Card padding="none" className="overflow-hidden" data-id="element-2955" data-mp="j6w3dkjo6okj">
      <div className="h-1.5 bg-gradient-to-r from-violet-500 via-purple-500 to-indigo-500" data-id="element-2956" data-mp="j8gzvfpjohl1" />
      <div className="p-4 md:p-6" data-id="element-2957" data-mp="y1aq9w6cui4h">
        <CardHeader className="px-0 pt-0 flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-2958" data-mp="t3r2o5nroszv">
          <CardTitle className="flex items-center gap-2" data-id="element-2959" data-mp="5dwqlqb9v8go">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg shadow-violet-500/20" data-id="element-2960" data-mp="5cd9jxhsktdu">
              <StethoscopeIcon className="w-5 h-5 text-white" data-id="element-2961" data-mp="f49bjjn013nc" />
            </div>
            <div data-id="element-2962" data-mp="adsvdrrsn3xa">
              <span className="block" data-id="element-2963" data-mp="ad9ca7qeq9ho">Ingresos por Doctor y Terapeuta</span>
              <span className="text-xs font-normal text-gray-500 dark:text-gray-400" data-id="element-2964" data-mp="aby7q5p50xpi">
                Citas completadas
              </span>
            </div>
          </CardTitle>
          <div className="flex items-center gap-2" data-id="element-2965" data-mp="3du9g2wd9zxzm">
            {canDownloadPdf && revenueByDoctorStats.length > 0 && <button onClick={() => {
            exportRevenueRankingToPdf(revenueByDoctorStats, 'Ingresos por Doctor', {
              from: revDoctorDateFrom,
              to: revDoctorDateTo
            });
          }} className="p-2 text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2966" data-mp="2h11ma5pdel9b">
                <DownloadIcon className="w-4 h-4" data-id="element-2967" data-mp="2zt43pj9u65km" />
              </button>}
            <Badge className="bg-gradient-to-r from-violet-500 to-purple-600 text-white py-1.5 px-3 text-sm shadow-lg shadow-violet-500/20 border-none" data-id="element-2968" data-mp="34rxh7bp4hwks">
              {formatCurrency(revenueByDoctorStats.reduce((sum, item) => sum + item.amount, 0))}
            </Badge>
          </div>
        </CardHeader>

        <div className="flex flex-wrap items-center gap-2 mt-4 mb-5" data-id="element-2969" data-mp="1ehcg5qhdpfyz">
          <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2970" data-mp="1jf464eygqs0j">
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDoctorDateFrom} onChange={e => setRevDoctorDateFrom(e.target.value)} data-id="element-2971" data-mp="1bxft8gvu0mbz" />
            <span className="text-gray-300 text-xs" data-id="element-2972" data-mp="126bffkhw77iq">→</span>
            <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={revDoctorDateTo} onChange={e => setRevDoctorDateTo(e.target.value)} data-id="element-2973" data-mp="x7qdngmzsfs6" />
          </div>
          <select className="text-xs border rounded-lg p-1.5 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 focus:ring-violet-500 text-gray-700 dark:text-gray-300" value={revDoctorSelectedId} onChange={e => setRevDoctorSelectedId(e.target.value)} data-id="element-2974" data-mp="1pwxmb8g5ulr5">
            <option value="all" data-id="element-2975" data-mp="9h4hammcojy0">Todos los doctores</option>
            {doctors.map(doc => <option key={doc.id} value={doc.id} data-id="element-2976" data-mp="9f1hxbzb5egm">
                {getDoctorPrefix(doc.gender, getSpecialty(doc.specialtyId)?.name)}{' '}
                {doc.name}
              </option>)}
          </select>
        </div>

        <CardContent className="px-0 pb-0" data-id="element-2977" data-mp="bvrf5a3nuw1s">
          {revenueByDoctorStats.length > 0 && <div className="mb-6" data-id="element-2978" data-mp="btos8yxt1j1a">
              <ResponsiveContainer width="100%" height={240} data-id="element-2979" data-mp="3t4xz3ktomrj6">
                <BarChart data={revenueByDoctorStats.slice(0, 6)} layout="vertical" margin={{
              left: 0,
              right: 10
            }} data-id="element-2980" data-mp="3t6iqx2om3wdc">
                  <defs data-id="element-2981" data-mp="3kb2uqf0dx54l">
                    <linearGradient id="doctorRevenueGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-2982" data-mp="3k9rom8jgvlab">
                      <stop offset="0%" stopColor="#8b5cf6" data-id="element-2983" data-mp="3p3vwqv4gxzcz" />
                      <stop offset="100%" stopColor="#6366f1" data-id="element-2984" data-mp="3aegb1tjsok45" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor2} horizontal={true} vertical={false} data-id="element-2985" data-mp="3fd9ndinci52q" />
                  <XAxis type="number" stroke={chartAxisColor2} fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2986" data-mp="31ds2vfh31dz6" />
                  <YAxis dataKey="name" type="category" stroke={chartAxisColor2} fontSize={12} tickLine={false} axisLine={false} width={120} tick={{
                fill: chartTickColor2,
                fontWeight: 500
              }} data-id="element-2987" data-mp="2mrb98lkjd5uh" />
                  <Tooltip contentStyle={{
                backgroundColor: chartTooltipBg2,
                border: chartTooltipBorder2,
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                padding: '10px 14px',
                color: chartTooltipColor2
              }} formatter={(value: number) => [<span style={{
                fontWeight: 'bold'
              }} data-id="element-2989" data-mp="3ehpig4hlttzt">{formatCurrency(value)}</span>, 'Ingresos']} data-id="element-2988" data-mp="2vovdm3dp3i9b" />
                  <Bar dataKey="amount" fill="url(#doctorRevenueGrad)" radius={[0, 6, 6, 0]} name="Ingresos" data-id="element-2990" data-mp="3agdzuix5e914" />
                </BarChart>
              </ResponsiveContainer>
            </div>}
          <div className="space-y-4" data-id="element-2991" data-mp="3jhvd7tj67wm4">
            {revenueByDoctorStats.map((doctor, index) => {
            const maxAmount = Math.max(...revenueByDoctorStats.map(d => d.amount), 1);
            const medals = ['🥇', '🥈', '🥉'];
            const medal = index < 3 ? medals[index] : null;
            return <div key={doctor.name} className="flex items-center gap-3 px-1 group" data-id="element-2992" data-mp="13pldvxoqtrt4" data-mpi={index}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-sm ${index === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white' : index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-white' : index === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' : 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 border border-violet-100 dark:border-violet-800'}`} data-id="element-2993" data-mp="18mbwaro0szhp">
                    {medal || index + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-2994" data-mp="3ocol6b2k2y3l">
                    <div className="flex justify-between mb-1.5" data-id="element-2995" data-mp="3tckgzn13nj82">
                      <div className="flex items-center gap-2 min-w-0" data-id="element-2996" data-mp="3ltt9b5y0yqyb">
                        <span className="text-[15px] font-semibold text-gray-800 dark:text-gray-200 truncate" data-id="element-2997" data-mp="3ls0audxkfd1e">
                          {doctor.name}
                        </span>
                        <span className="text-[11px] text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0" data-id="element-2998" data-mp="3bu2fl6hagfza">
                          {doctor.count} citas
                        </span>
                      </div>
                      <span className="text-[15px] font-bold text-violet-600 dark:text-violet-400 ml-2 whitespace-nowrap" data-id="element-2999" data-mp="2y3qmp5cvqd73">
                        {formatCurrency(doctor.amount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2.5 overflow-hidden" data-id="element-3000" data-mp="2o4868yw6h9js">
                      <div className="h-full rounded-full transition-all duration-1000 ease-out bg-gradient-to-r from-violet-500 to-indigo-400" style={{
                    width: `${doctor.amount / maxAmount * 100}%`
                  }} data-id="element-3001" data-mp="2j92mo6q906yv" />
                    </div>
                  </div>
                </div>;
          })}
            {revenueByDoctorStats.length === 0 && <div className="text-center py-10 text-gray-400 text-sm italic" data-id="element-3002" data-mp="v071hivzeph9">
                No se encontraron ingresos de doctores para este período.
              </div>}
          </div>
        </CardContent>
      </div>
    </Card>;
}